/* ============================================================
   orderActionRecovery — TASK-250 (ca3a0011): pure classifier that
   turns a failed customer confirm/dispute POST into a recovery
   affordance instead of a raw dead-end error line.

   Why: the three real stale-state failures of the confirm/dispute
   block return *English* server strings («The result has already
   been confirmed.», «Cannot dispute order in status '…'.»), which
   the block printed verbatim while still offering the same submit
   buttons. The canonical case is the P2 auto-confirm job (audit5-B2)
   confirming the result while the customer has the page open — the
   POST then fails although the outcome the customer wanted already
   happened, and the only correct recovery is a refresh, not a retry.

   Contract:
   - `recovery: 'refresh'` → the local order view is stale; the block
     renders «Обновить заказ», which triggers the existing onDone →
     load() refresh (no new API surface);
   - `recovery: null` → transient/client-side problem; the existing
     submit button IS the retry, the message just says so in Russian;
   - unknown errors keep their original message (validation and
     already-localized lines pass through untouched).

   Pure, node-testable, no React dependency. Light theme per
   DESIGN-SYSTEM.md (rendering stays in OrdersPage).
   ============================================================ */

/** What the customer can do about the failure. `null` = message only. */
export type OrderActionRecovery = 'refresh';

export interface OrderActionFailureView {
  /** Friendly Russian line to render in the danger slot. */
  message: string;
  /** Stale local state → offer the refresh affordance. */
  recovery: OrderActionRecovery | null;
  /** Button label for the recovery action (null when recovery is null). */
  recoveryLabel: string | null;
}

/** Label for the refresh affordance — single source for UI + tests. */
export const ORDER_ACTION_REFRESH_LABEL = 'Обновить заказ';

/** Client-side validation / already-localized failures: no recovery hook. */
export function plainActionFailure(message: string): OrderActionFailureView {
  return { message, recovery: null, recoveryLabel: null };
}

/** TASK-250: server-side English stale markers, matched case-insensitively. */
const ALREADY_CONFIRMED = /already been confirmed/i;
const WRONG_STATUS = /Cannot (?:confirm result|dispute order) in status/i;
const ORDER_NOT_FOUND = /^Order not found\.$/i;

/** Transient transport problems — the submit button is the retry. */
const SERVER_5XX = /HTTP 5\d\d/;
const NETWORK = /Failed to fetch|NetworkError|fetch failed|load failed|NetworkError when attempting/i;

/**
 * Classify a confirm/dispute failure.
 * @param action which leg failed (confirm result / open dispute).
 * @param error thrown value from the API client (`Error`, string, unknown).
 */
export function buildOrderActionFailure(
  action: 'confirm' | 'dispute',
  error: unknown,
): OrderActionFailureView {
  const raw = error instanceof Error ? error.message : typeof error === 'string' ? error : '';

  // Stale-state: the outcome already happened (P2 auto-confirm job,
  // another tab, a dispute opened first). The page's copy is outdated —
  // refresh closes the loop; the post-refresh view model stops offering
  // the action on its own (confirmResult only while Printed-unconfirmed).
  if (ALREADY_CONFIRMED.test(raw)) {
    return {
      message: 'Результат уже подтверждён — возможно, автоматически по сроку подтверждения или в другом окне.',
      recovery: 'refresh',
      recoveryLabel: ORDER_ACTION_REFRESH_LABEL,
    };
  }
  if (WRONG_STATUS.test(raw)) {
    return {
      message: `Статус заказа изменился с момента открытия страницы — ${action === 'confirm' ? 'подтвердить результат' : 'открыть спор'} сейчас нельзя.`,
      recovery: 'refresh',
      recoveryLabel: ORDER_ACTION_REFRESH_LABEL,
    };
  }
  if (ORDER_NOT_FOUND.test(raw)) {
    return {
      message: 'Заказ не найден — возможно, он был удалён. Обновите список заказов.',
      recovery: 'refresh',
      recoveryLabel: ORDER_ACTION_REFRESH_LABEL,
    };
  }

  // Transient transport: retry is the same button, message just says so.
  if (SERVER_5XX.test(raw) || NETWORK.test(raw)) {
    return {
      message: 'Не удалось отправить — похоже, проблема с соединением. Попробуйте ещё раз.',
      recovery: null,
      recoveryLabel: null,
    };
  }

  // Unknown / already-localized (validation, RFC7807 titles in Russian):
  // keep the original line so nothing regresses information-wise.
  return plainActionFailure(raw || 'Не удалось отправить');
}
